import BaseMaterialIconButton from "@/components/reusables/BaseMaterialIconButton";
import { ReactComponent as EditSVG } from "@assets/icons/ic_edit.svg";
import { ReactComponent as TrashIcon } from "@assets/icons/ic_trash.svg";
import { ReactComponent as CollectionsIcon } from "@assets/icons/sidebar/ic_folders.svg";
import QuantityCell
    from "@components/Dashboard/pages/Sales/Orders/components/MutationLayout/parts/ProductSection/QuantityCell";
import TotalCell
    from "@components/Dashboard/pages/Sales/Orders/components/MutationLayout/parts/ProductSection/TotalCell";
import {
    isGroup,
    isLine,
    LineGroupUnion,
    LineSchema,
    ReservationSchema,
    SaleOrdersFormTyping,
} from "@components/Dashboard/pages/Sales/Orders/components/MutationLayout/types";
import { Draggable, Droppable } from "@hello-pangea/dnd";
import { ArrayElementType, calculateOrderPrice, cn, normalizePrice } from "@helpers/utils";
import BaseBadge from "@reusables/BaseBadge";
import BaseTableV2 from "@reusables/BaseTableV2";
import BaseTooltip from "@reusables/BaseTooltip";
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { ALargeSmall, ExternalLink, GripVertical, MessageSquareText } from "lucide-react";
import { memo } from "react";
import { useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

type LinesRendererProps = {
    accessor: "lines" | `lines.${number}.lines`;
    lines: LineGroupUnion[];
    onLineEditModalOpen?: (lineIndex: number, levelAccessor: string, line: LineGroupUnion) => void;
    combineDraggableId?: string;
};

/**
 * Calculates total quantities for specific products across all lines (including nested group lines).
 * @param allLines - The full lines array from the form
 * @param productIds - Set of product IDs to calculate totals for
 * @returns Map of product ID to total quantity
 */
function calculateProductTotals(
    allLines: LineGroupUnion[],
    productIds: Set<number>,
): Map<number, number> {
    const totals = new Map<number, number>();

    // Initialize all requested products with 0
    productIds.forEach((id) => totals.set(id, 0));

    const processLine = (line: LineSchema) => {
        const productId = line.product?.id;
        if (productId && productIds.has(productId)) {
            const current = totals.get(productId) ?? 0;
            totals.set(productId, current + (line.quantity ?? 0));
        }
    };

    allLines.forEach((line) => {
        if (isLine(line)) {
            processLine(line);
        } else if (isGroup(line)) {
            line.lines.forEach((groupLine) => processLine(groupLine));
        }
    });

    return totals;
}

/**
 * Syncs reservations after line deletion - caps quantities and removes orphaned reservations.
 * @param reservations - Current reservations array
 * @param allLines - All lines after deletion
 * @param deletedProductIds - Set of product IDs that were in the deleted line(s)
 * @returns Updated reservations array
 */
function syncReservationsAfterDeletion(
    reservations: ReservationSchema[],
    allLines: LineGroupUnion[],
    deletedProductIds: Set<number>,
): ReservationSchema[] {
    if (deletedProductIds.size === 0 || reservations.length === 0) {
        return reservations;
    }

    // Calculate new totals for affected products
    const newTotals = calculateProductTotals(allLines, deletedProductIds);

    // Update reservations
    return reservations
        .map((reservation) => {
            const newTotal = newTotals.get(reservation.product.id);

            // Not affected by this deletion
            if (newTotal === undefined) return reservation;

            // Product no longer exists in lines - remove reservation
            if (newTotal === 0) return null;

            // Cap reservation quantity to new total if it exceeds
            if (reservation.quantity > newTotal) {
                return { ...reservation, quantity: newTotal };
            }

            return reservation;
        })
        .filter((r): r is ReservationSchema => r !== null);
}

export default memo(function LinesRenderer({
                                               accessor,
                                               lines,
                                               onLineEditModalOpen,
                                               combineDraggableId,
                                           }: LinesRendererProps) {
    const { t } = useTranslation("", { keyPrefix: "sales.orders.general.orders" });

    const { getValues, setValue } = useFormContext<SaleOrdersFormTyping>();

    console.log(`LinesRenderer with accessor ${accessor} is rerendering...`);

    const columnHelper = createColumnHelper<ArrayElementType<typeof lines>>();

    const table = useReactTable({
        data: lines,
        columns: [
            columnHelper.display({
                id: "grip",

                header: t("columns.0") + "",
                cell: () => <GripVertical size={16} color={"#D9D6DA"} />,
                maxSize: 20,
                meta: {
                    forGroup: true,
                },
            }),
            columnHelper.accessor((row) => (isGroup(row) ? row.name : row.product.code), {
                id: "product_code",
                cell: (info) => {
                    const row = info.row.original;
                    if (isLine(row)) {
                        return (
                            <div className="flex items-center">
                                {/*<div className="self-center w-[2px] h-[20px] bg-[#D9D6DA] rounded-sm mr-2" />*/}
                                <div>{row.product.code}</div>
                                {row.comment && (
                                    <BaseTooltip title={row.comment} placement="top">
                                        <MessageSquareText
                                            className="w-5 ml-2 text-[#5A5587] hover:text-accent cursor-pointer" />
                                    </BaseTooltip>
                                )}
                            </div>
                        );
                    } else {
                        return null;
                    }
                },
                maxSize: 150,
            }),

            columnHelper.accessor((row) => (isLine(row) ? row.product.name : ""), {
                id: "product_name",
                cell: (info) => {
                    const row = info.row.original;

                    if (isLine(row)) {
                        if (row.alternative_name) {
                            return (
                                <div className="flex items-center space-x-2">
                                    <div>{row.alternative_name}</div>
                                    <BaseMaterialIconButton
                                        tip={
                                            <Trans
                                                t={t}
                                                i18nKey="nameCell.originalName"
                                                values={{
                                                    name: row.product.name,
                                                }}
                                                components={{ b: <b /> }}
                                            />
                                        }
                                        icon={<ALargeSmall size={14} />}
                                        size="tiny"
                                        style={"dim"}
                                    />
                                </div>
                            );
                        }


                        return row.product.name;
                    }

                    return (
                        <div
                            className={"flex flex-col pl-3 space-y-[8px]"}
                            style={{ borderLeft: "3px solid #B1B2E1" }}
                        >
                            <div className="flex items-center space-x-2">
                                <div className="font-semibold">{row.name}</div>
                                {(row.module === "collection" || row.module === "bom") && (
                                    <BaseBadge
                                        className="px-2 bg-[#CDCFFD] rounded-xl cursor-pointer hover:opacity-70"
                                        onClick={() => {
                                            if (row.module === "collection") {
                                                window.open(
                                                    `/dashboard/inventory/collections/${row.module_entity_id}/edit`,
                                                );
                                            } else {
                                                window.open(
                                                    `/dashboard/bill-of-materials/${row.module_entity_id}/details`,
                                                );
                                            }
                                        }}
                                    >
                                        <div className="flex items-center">
                                            <CollectionsIcon className="w-[12px] mr-1 bg-[#CDCFFD] text-accent" />
                                            <span>{t(`groups.types.${row.module}`)}</span>
                                            <ExternalLink
                                                size={10}
                                                className="ml-2 text-sm text-accent"
                                            />
                                        </div>
                                    </BaseBadge>
                                )}
                            </div>
                            {!!row.comment && (
                                <div className={"text-[#5A5587] text-sm font-thin"}>
                                    {row.comment}
                                </div>
                            )}
                        </div>
                    );
                },
                meta: {
                    forGroup: true,
                },
            }),
            columnHelper.accessor((row) => (isLine(row) ? row.quantity : 0), {
                id: "quantity",
                cell: (info) => {
                    const row = info.row.original;

                    if (!isLine(row)) return "";

                    return <QuantityCell line={row} />;
                },

                maxSize: 120,
            }),
            columnHelper.accessor((row) => (isLine(row) ? normalizePrice(row.unit_price) : ""), {
                id: "unit_price",
                cell: (info) => {
                    const row = info.row.original;

                    if (!isLine(row)) return "";

                    return (
                        <div className="flex items-center">
                            {row.unit_price ? normalizePrice(row.unit_price) : "-"}
                        </div>
                    );
                },
                maxSize: 120,
            }),
            columnHelper.accessor((row) => (isLine(row) ? row.discount : 0), {
                id: "discount",
                cell: (info) => {
                    const row = info.row.original;
                    if (!isLine(row)) return "";

                    return <div className="flex">{row.discount ? `${row.discount}%` : "-"}</div>;
                },
                maxSize: 120,
            }),
            columnHelper.accessor((row) => (isLine(row) ? row.tax?.rate : 0), {
                id: "tax",
                cell: (info) => {
                    const row = info.row.original;
                    if (!isLine(row)) return "";

                    return <div className="flex">{row.tax?.rate ? `${row.tax.rate}%` : "-"}</div>;
                },
                maxSize: 120,
            }),
            columnHelper.accessor(
                (row) =>
                    isLine(row) &&
                    normalizePrice(
                        calculateOrderPrice(
                            row.quantity ?? 0,
                            row.unit_price ?? 0,
                            row.discount ?? 0,
                        ),
                    ),
                {
                    id: "total",
                    cell: (info) => {
                        const row = info.row.original;
                        const index = info.row.index;

                        if (!isLine(row)) return "";

                        // If row.data exists or row is a group header (without data), show TotalCell
                        return <TotalCell line={row} />;
                    },
                    maxSize: 150,
                },
            ),
            columnHelper.display({
                id: "actions",
                cell: (info) => {
                    const row = info.row.original;
                    const index = info.row.index;
                    const isEditable = getValues("is_editable") === true;
                    if (!isEditable) return null;
                    return (
                        <div
                            className={`flex justify-end space-x-2 ${
                                isGroup(row) ? "text-[#5A5587]" : "text-tables-secondaryIcon"
                            }`}
                        >
                            <EditSVG
                                className="hover:text-tables-highlightedIcon cursor-pointer"
                                onClick={() => onLineEditModalOpen?.(index, accessor, row)}
                            />
                            <TrashIcon
                                className="hover:text-tables-highlightedIcon cursor-pointer"
                                onClick={() => handleRemove(index)}
                            />
                        </div>
                    );
                },
                maxSize: 100,
                meta: {
                    forGroup: true,
                },
            }),
        ],
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getRowCanExpand: (row) => isGroup(row.original),
        getRowId: (row, index) => accessor + ">" + index,
    });

    const handleRemove = (index: number) => {
        const line = lines[index];
        const deletedLines = getValues("deleted_lines") ?? [];
        const currentReservations = getValues("reservations") ?? [];

        // Collect product IDs being deleted for reservation sync
        const deletedProductIds = new Set<number>();

        if (isGroup(line)) {
            const groupItemIds = line.lines.map((item) => item.id);

            // Collect product IDs from group lines
            line.lines.forEach((item) => {
                if (item.product?.id) deletedProductIds.add(item.product.id);
            });

            // Update deleted lines with group item IDs
            setValue("deleted_lines", [...deletedLines, ...groupItemIds]);
        } else {
            // Collect product ID from single line
            if (line.product?.id) deletedProductIds.add(line.product.id);

            // Add the single item's ID to deleted lines if it exists
            if (line.id) {
                setValue("deleted_lines", [...deletedLines, line.id]);
            }
        }

        // Remove the line from the lines array
        const remainingLines = lines.filter((_, fI) => fI !== index);
        setValue(accessor, remainingLines);

        // Sync reservations: get ALL lines from root to calculate proper totals
        if (deletedProductIds.size > 0 && currentReservations.length > 0) {
            // Get the full lines array from form to calculate totals across all levels
            const allLines = getValues("lines");

            // Since we just updated the accessor, we need to compute what the full lines will look like
            // If we're at root level (accessor === "lines"), use remainingLines directly
            // Otherwise, the full lines already reflect the nested update
            const effectiveLines = accessor === "lines" ? remainingLines : allLines;

            const updatedReservations = syncReservationsAfterDeletion(
                currentReservations,
                effectiveLines,
                deletedProductIds,
            );

            setValue("reservations", updatedReservations);
        }
    };

    return (
        <Droppable
            droppableId={accessor}
            isCombineEnabled
            getContainerForClone={() => document.body}
            renderClone={(provided, snapshot, rubric) => {
                const row = table.getRowModel().rows[rubric.source.index];

                const isRowGroup = row.original && isGroup(row.original);
                const cells = row
                    .getVisibleCells()
                    .filter((cell) => !isRowGroup || (cell.column.columnDef.meta as any)?.forGroup);

                return (
                    <BaseTableV2.Body className="space-y-2">
                        <div
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            ref={provided.innerRef}
                            className={cn(
                                "bg-white border-2 border-solid border-[#ECEDFE] rounded-md",
                            )}
                        >
                            <BaseTableV2.Row
                                className={cn(isRowGroup && "min-h-[40px] bg-[#ECEDFE]")}
                            >
                                {cells.map((cell, index) => (
                                    <BaseTableV2.Cell
                                        key={cell.id}
                                        {...cell.column.columnDef}
                                        className={cn(
                                            isRowGroup && row.getCanExpand()
                                                ? "self-center border-2 border-[#ECEDFE]"
                                                : "",
                                        )}
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </BaseTableV2.Cell>
                                ))}
                            </BaseTableV2.Row>
                        </div>
                    </BaseTableV2.Body>
                );
            }}
        >
            {(provided) => (
                <BaseTableV2.Body
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="space-y-2"
                >
                    {table.getRowModel().rows.map((row, index) => {
                        const draggableId = accessor + ">" + index;

                        return (
                            <Draggable key={row.id} draggableId={draggableId} index={index}>
                                {(provided, snapshot) => {
                                    const isRowGroup = row.original && isGroup(row.original);
                                    const cells = row
                                        .getVisibleCells()
                                        .filter(
                                            (cell) =>
                                                !isRowGroup ||
                                                (cell.column.columnDef.meta as any)?.forGroup,
                                        );

                                    return (
                                        <>
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                className={cn(
                                                    combineDraggableId === draggableId &&
                                                    "bg-[#ECEDFE] bg-opacity-50",
                                                    isRowGroup &&
                                                    "border-2 border-solid border-[#ECEDFE] rounded-md",
                                                )}
                                            >
                                                <BaseTableV2.Row
                                                    className={cn(
                                                        isRowGroup && "min-h-[40px] bg-[#ECEDFE]",
                                                    )}
                                                >
                                                    {cells.map((cell) => (
                                                        <BaseTableV2.Cell
                                                            key={cell.id}
                                                            {...cell.column.columnDef}
                                                            {...(cell.column.columnDef.id ===
                                                                "grip" && {
                                                                    ...provided.dragHandleProps,
                                                                })}
                                                            className={cn(
                                                                isRowGroup && row.getCanExpand()
                                                                    ? "self-center border-2 border-[#ECEDFE]"
                                                                    : "",
                                                            )}
                                                        >
                                                            {flexRender(
                                                                cell.column.columnDef.cell,
                                                                cell.getContext(),
                                                            )}
                                                        </BaseTableV2.Cell>
                                                    ))}
                                                </BaseTableV2.Row>
                                                {row.getCanExpand() && isGroup(row.original) && (
                                                    <>
                                                        {row.original.lines.length ? (
                                                            <LinesRenderer
                                                                // TODO: improve validation to avoid `any`
                                                                accessor={
                                                                    `${accessor}.${row.index}.lines` as any
                                                                }
                                                                lines={row.original.lines}
                                                                onLineEditModalOpen={
                                                                    onLineEditModalOpen
                                                                }
                                                                combineDraggableId={
                                                                    combineDraggableId
                                                                }
                                                            />
                                                        ) : (
                                                            <div className="flex items-center justify-center h-[70px]">
                                                                <div
                                                                    className="text-center text-sm text-[#5A5587] opacity-50">
                                                                    {t("groups.emptyBanner")}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </>
                                    );
                                }}
                            </Draggable>
                        );
                    })}
                    {provided.placeholder}
                </BaseTableV2.Body>
            )}
        </Droppable>
    );
});
